import React, {useEffect, useMemo, useState} from 'react';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import './ProductionsAndExports.scss';
import ViewTemplates from './manage-template/view-templates/ViewTemplates';
import ProductionsAndExportsDetails from './productions-and-exports-details/ProductionsAndExportsDetails';
import ArrSingleSelectDropdown from '../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../shared/types';
import {useViewHomeSecurityGroup} from '../../hooks/productions/useProductions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

interface TabData {
  label: string;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`productions-and-exports-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`productions-and-exports-div-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

const ProductionsAndExports: React.FC = () => {
  const SECURITY_GROUP_DROPDOWN_CUSTOM_WIDTH = 15;

  const {data: securityGroupData, isLoading: isLoadingSecurityGroup} =
    useViewHomeSecurityGroup();
  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(null);
  const [securityGroupId, setSecurityGroupId] = useState<number>(0);
  const [value, setValue] = useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Initialize selected security group from securityGroupData
  useEffect(() => {
    if (securityGroupData?.securityGroups?.length) {
      const selected = securityGroupData.securityGroups.find(
        (group) => group.isSelected
      );
      if (selected?.value) {
        const id = Number(selected.value);
        setSelectedSecurityGroup({
          id: selected.value, // Internal value (value) as string
          name: selected.text ?? '', // Display text
        });
        setSecurityGroupId(id);
      }
    }
  }, [securityGroupData]);

  const onSecurityGroupChange = (val: ISelectItem | null) => {
    if (val?.id) {
      const id = Number(val.id);
      if (!Number.isNaN(id) && id > 0) {
        setSelectedSecurityGroup(val);
        setSecurityGroupId(id);
        // Switch to the first tab to reflect changes immediately
        setValue(0);
      }
    }
  };

  // Get the actual security group ID to use (from state or API response)
  const currentSecurityGroupId = useMemo(() => {
    const id =
      securityGroupId > 0
        ? securityGroupId
        : Number(securityGroupData?.selectedSecurityGrpId ?? 0);
    return id;
  }, [securityGroupId, securityGroupData?.selectedSecurityGrpId]);

  const tabData: TabData[] = useMemo(
    () => [
      {
        label: t('productionsAndExports.productionsAndExportsTab'),
      },
      {
        label: t('productionsAndExports.manageTemplatesTab'),
      },
    ],
    []
  );

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const focusTab = (index: number) => {
    const tab = document.getElementById(`simple-tab-${index}`);
    if (tab) {
      requestAnimationFrame(() => {
        tab.focus();
      });
    }
  };

  const handleTabsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    const activeTabId = activeElement?.id;
    if (!activeTabId?.startsWith('simple-tab-')) {
      return;
    }
    const currentIndex = Number(activeTabId.replace('simple-tab-', ''));
    if (Number.isNaN(currentIndex)) {
      return;
    }
    const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
    const nextTab = document.getElementById(`simple-tab-${nextIndex}`);
    if (nextTab) {
      event.preventDefault();
      focusTab(nextIndex);
    }
  };

  const handleDropdownKeyDownCapture = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      const lastTabIndex = tabData.length - 1;
      focusTab(lastTabIndex);
    }
  };

  return (
    <Box
      className={'productions-container'}
      data-testid="productions-container"
    >
      <Box className="productions-header" data-testid="productions-header">
        <Typography className="title-text" data-testid="title-text">
          {t('productionsAndExports.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('productionsAndExports.title')}
          trigger={
            <InfoIcon fontSize="medium" className="info-icon" tabIndex={0} />
          }
          closeTooltipOnEscPress
        >
          <Trans
            i18nKey="productionsAndExports.info.tooltipContent"
            components={{
              br: <br data-testid={`productions-and-exports-br`} />,
              strong: <strong data-testid={`productions-and-exports-strong`} />,
              p: <p data-testid={`productions-and-exports-p`} />,
            }}
          />
        </ArrTooltipWrapper>
      </Box>
      <Box className="productions-content">
        <Box className="tabs-section" data-testid="tabs-section">
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="productions And export tabs"
            className="tabs-content"
            data-testid="tabs"
            onKeyDown={handleTabsKeyDown}
          >
            {tabData.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                className="tab-content"
                data-testid={`tab-${index}`}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
          <Box
            className="security-group-container"
            onKeyDownCapture={handleDropdownKeyDownCapture}
          >
            <Typography
              className="security-group-label"
              data-testid={`productions-and-exports-security-group`}
            >
              {t('productionsAndExports.securityGroup')}
            </Typography>
            <ArrSingleSelectDropdown
              dropdownLabel=""
              dropdownItems={
                securityGroupData?.securityGroups?.map((group) => ({
                  id: group.value, // Internal value (value) for internal use
                  name: group.text ?? '', // Display text (bound to dropdown)
                })) ?? []
              }
              selectedValues={selectedSecurityGroup}
              dropdownItemKey={'id'} // Uses 'id' (value) for internal matching
              dropdownItemDisplay={'name'} // Displays 'name' (text) in dropdown
              onSelectionChange={onSecurityGroupChange}
              dataTestId="asd-production-export-select-security-group"
              sizeVariant="medium"
              customWidth={SECURITY_GROUP_DROPDOWN_CUSTOM_WIDTH}
              toolTip={false}
              keyPressActive={true}
              searchable
              showNoResultsText
            />
          </Box>
        </Box>
        <Box className="tabs-child">
          {tabData.map((tab, index) => (
            <TabPanel
              key={index === 0 ? `tab-${currentSecurityGroupId}` : tab.label}
              value={value}
              index={index}
              data-testid={`tabpanel-${index}`}
            >
              {index === 0 ? (
                currentSecurityGroupId > 0 &&
                !isLoadingSecurityGroup && (
                  <ProductionsAndExportsDetails
                    key={`details-${currentSecurityGroupId}`}
                    securityGroupId={currentSecurityGroupId}
                    projectId={securityGroupData?.currentProjectId ?? 0}
                  />
                )
              ) : (
                <ViewTemplates
                  key={`templates-${currentSecurityGroupId}`}
                  securityGroupId={currentSecurityGroupId}
                />
              )}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductionsAndExports;
